import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { fetchPortalApplicationById } from "../../services/api";
import { useToast } from "../../contexts/ToastContext";
import "../../styles/portal-view.css";

function PortalApplicationView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadApplication = async () => {
      setLoading(true);
      try {
        const res = await fetchPortalApplicationById(id);
        if (res.success && res.application) {
          setApplication(res.application);
        } else {
          showToast("Application not found.", "error");
          navigate("/portal/dashboard");
        }
      } catch (err) {
        console.error("Failed to load application:", err);
        showToast("Failed to load application details.", "error");
        navigate("/portal/dashboard");
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      loadApplication();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const formatCost = (cost) => {
    if (!cost && cost !== 0) return "—";
    const num = Number(String(cost).replace(/,/g, ""));
    if (isNaN(num)) return "—";
    return `$${num.toLocaleString("en-US", { minimumFractionDigits: 0 })}`;
  };

  const formatPhone = (phone) => {
    if (!phone) return "—";
    const digits = String(phone).replace(/\D/g, "").substring(0, 10);
    if (digits.length > 6)
      return `(${digits.substring(0, 3)}) ${digits.substring(3, 6)}-${digits.substring(6)}`;
    if (digits.length > 3)
      return `(${digits.substring(0, 3)}) ${digits.substring(3)}`;
    return digits || "—";
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getStatusClass = (status) => {
    switch (status?.toLowerCase()) {
      case "submitted": return "pv-status--pending";
      case "review fee due": return "pv-status--pending";
      case "review fee paid": return "pv-status--approved";
      case "pending": return "pv-status--pending";
      case "village fees due": return "pv-status--pending";
      case "village fees paid": return "pv-status--approved";
      case "approved": return "pv-status--approved";
      case "finalized": return "pv-status--finalized";
      case "void": return "pv-status--void";
      default: return "pv-status--pending";
    }
  };

  if (loading) {
    return (
      <div className="pv-loading">
        <div className="pv-loading-spinner" />
        <span>Loading application details...</span>
      </div>
    );
  }

  if (!application) return null;

  const contractors = application.contractors || [];
  const location = application.location || {};
  const files = location.files || [];
  const standardReports = application.standardReport || [];

  return (
    <div className="pv-container">
      {/* Back Button & Header */}
      <div className="pv-top-bar">
        <button className="pv-back-btn" onClick={() => navigate("/portal/dashboard")}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5" />
            <polyline points="12 19 5 12 12 5" />
          </svg>
          Back to Applications
        </button>
      </div>

      {/* Header Card */}
      <div className="pv-header-card">
        <div className="pv-header-main">
          <div className="pv-header-info">
            <h2 className="pv-header-title">
              {application.firstName} {application.lastName}
            </h2>
            <p className="pv-header-subtitle">
              Application #{String(application.applicantId).padStart(2, "0")}
              {application.permitNumber && (
                <span className="pv-header-permit">
                  &nbsp;·&nbsp;Permit #{application.permitNumber}
                </span>
              )}
            </p>
          </div>
          <div className={`pv-status-badge ${getStatusClass(application.status)}`}>
            {application.status || "Pending"}
          </div>
        </div>
        <div className="pv-header-meta">
          <div className="pv-meta-item">
            <span className="pv-meta-label">Submitted</span>
            <span className="pv-meta-value">{formatDate(application.date)}</span>
          </div>
          <div className="pv-meta-item">
            <span className="pv-meta-label">Permit Type</span>
            <span className="pv-meta-value">{location.permitType || "—"}</span>
          </div>
          <div className="pv-meta-item">
            <span className="pv-meta-label">Project Address</span>
            <span className="pv-meta-value">{location.address || "—"}</span>
          </div>
        </div>
      </div>

      {/* Administration Note (if present, shown as a highlight banner above the grid) */}
      {(application.adminComment || application.reason) && (
        <div className="pv-admin-note my-3">
          <strong>Administration Note: </strong>
          {application.adminComment || application.reason}
        </div>
      )}

      {/* Grid Layout containing Main Column and Side Column */}
      <div className="pv-grid">
        <div className="pv-main-col">
          {/* Section 1: Applicant Information */}
          <div className="pv-card">
            <h3 className="pv-card-title">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-1">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
              Applicant Information
            </h3>
            <div className="pv-field-grid">
              <div className="pv-field">
                <span className="pv-field-label">First Name</span>
                <span className="pv-field-value">{application.firstName || "—"}</span>
              </div>
              <div className="pv-field">
                <span className="pv-field-label">Last Name</span>
                <span className="pv-field-value">{application.lastName || "—"}</span>
              </div>
              <div className="pv-field">
                <span className="pv-field-label">Email</span>
                <span className="pv-field-value">{application.email || "—"}</span>
              </div>
              <div className="pv-field">
                <span className="pv-field-label">Phone</span>
                <span className="pv-field-value">{formatPhone(application.phoneNumber)}</span>
              </div>
              <div className="pv-field pv-field--full">
                <span className="pv-field-label">Address</span>
                <span className="pv-field-value">{application.address || "—"}</span>
              </div>
            </div>
            {application.notes && (
              <div className="mt-4">
                <span className="pv-field-label mb-2 d-block">Notes</span>
                <p className="pv-notes-text">{application.notes}</p>
              </div>
            )}
          </div>

          {/* Section 2: Location & Project Details */}
          <div className="pv-card">
            <h3 className="pv-card-title">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-1">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
              Location & Project Details
            </h3>
            <div className="pv-field-grid">
              <div className="pv-field pv-field--full">
                <span className="pv-field-label">Project Address</span>
                <span className="pv-field-value">{location.address || "—"}</span>
              </div>
              <div className="pv-field">
                <span className="pv-field-label">Permit Type</span>
                <span className="pv-field-value">{location.permitType || "—"}</span>
              </div>
              <div className="pv-field">
                <span className="pv-field-label">Estimated Cost</span>
                <span className="pv-field-value">{formatCost(location.cost)}</span>
              </div>
            </div>
            {location.description && (
              <div className="mt-4">
                <span className="pv-field-label mb-2 d-block">Project Description</span>
                <p className="pv-notes-text">{location.description}</p>
              </div>
            )}
          </div>

          {/* Section 3: Contractors */}
          <div className="pv-card">
            <h3 className="pv-card-title">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-1">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
              Contractors
            </h3>
            {contractors.length === 0 ? (
              <div className="pv-empty-state">
                <p>No contractors assigned to this application.</p>
              </div>
            ) : (
              contractors.map((contractor, index) => (
                <div key={index} className="pv-contractor-item">
                  <h4 className="pv-contractor-title">
                    Contractor {index + 1}
                    {contractor.trade && (
                      <span className="pv-trade-badge">{contractor.trade}</span>
                    )}
                  </h4>
                  <div className="pv-field-grid">
                    <div className="pv-field">
                      <span className="pv-field-label">Name</span>
                      <span className="pv-field-value">{contractor.individualName || "—"}</span>
                    </div>
                    <div className="pv-field">
                      <span className="pv-field-label">Contractor Email</span>
                      <span className="pv-field-value">{contractor.contractorEmail || "—"}</span>
                    </div>
                    <div className="pv-field">
                      <span className="pv-field-label">Phone</span>
                      <span className="pv-field-value">{formatPhone(contractor.phoneNumber)}</span>
                    </div>
                    <div className="pv-field pv-field--full">
                      <span className="pv-field-label">Address</span>
                      <span className="pv-field-value">{contractor.address || "—"}</span>
                    </div>
                    {contractor.licenseLink && contractor.licenseLink.length > 0 && (
                      <div className="pv-field pv-field--full">
                        <span className="pv-field-label">License Files</span>
                        <div className="pv-file-list mt-1">
                          {contractor.licenseLink.map((lic, licIdx) => (
                            <a
                              key={licIdx}
                              href={lic.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="pv-file-link"
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                <polyline points="14 2 14 8 20 8" />
                              </svg>
                              License {licIdx + 1}
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="pv-side-col">
          {/* Section 4: Fees Summary */}
          <div className="pv-card">
            <h3 className="pv-card-title">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-1">
                <line x1="12" y1="1" x2="12" y2="23" />
                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
              </svg>
              Fee Summary
            </h3>
            <div className="pv-fees-table">
              <div className="pv-fee-row">
                <span className="pv-fee-label">Permit Fee</span>
                <span className="pv-fee-value">{formatCost(application.permitFee)}</span>
              </div>
              <div className="pv-fee-row">
                <span className="pv-fee-label">Plan Review Fee</span>
                <span className="pv-fee-value">{formatCost(application.planReviewFee)}</span>
              </div>
              <div className="pv-fee-row">
                <span className="pv-fee-label">Inspections Fee</span>
                <span className="pv-fee-value">{formatCost(application.inspectionsFee)}</span>
              </div>
              <div className="pv-fee-row pv-fee-row--total">
                <span className="pv-fee-label">Total Fees</span>
                <span className="pv-fee-value">
                  {formatCost(
                    (Number(String(application.permitFee || 0).replace(/,/g, "")) || 0) +
                    (Number(String(application.planReviewFee || 0).replace(/,/g, "")) || 0) +
                    (Number(String(application.inspectionsFee || 0).replace(/,/g, "")) || 0)
                  )}
                </span>
              </div>
            </div>
          </div>

          {/* Section 5: Documents & Reports */}
          <div className="pv-card">
            <h3 className="pv-card-title">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-1">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
              </svg>
              Documents & Files
            </h3>
            {files.length > 0 && (
              <div className="mb-4">
                <span className="pv-sub-label mb-2 d-block">Uploaded Documents</span>
                <div className="pv-file-list">
                  {files.map((file, index) => (
                    <a
                      key={index}
                      href={file.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="pv-file-link"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <polyline points="14 2 14 8 20 8" />
                      </svg>
                      Document {index + 1}
                    </a>
                  ))}
                </div>
              </div>
            )}

            {standardReports.length > 0 && (
              <div className="mb-4">
                <span className="pv-sub-label mb-2 d-block">Permit Reports</span>
                <div className="pv-file-list">
                  {standardReports.map((file, index) => (
                    <a
                      key={index}
                      href={file.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="pv-file-link pv-file-link--report"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <polyline points="14 2 14 8 20 8" />
                        <line x1="16" y1="13" x2="8" y2="13" />
                        <line x1="16" y1="17" x2="8" y2="17" />
                      </svg>
                      Report {index + 1}
                    </a>
                  ))}
                </div>
              </div>
            )}

            {files.length === 0 && standardReports.length === 0 && (
              <div className="pv-empty-state">
                <p>No documents available for this application.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default PortalApplicationView;
